// Libraries
import React from 'react'
import { ModuleNames } from '../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../constants/localization.constants'
import Layout from '../../layouts/layout'

import ContentBlock from '../../global/contentBlock'
import ContentColumns from '../../global/contentColumns'
import ContentRows from '../../global/contentRows'
import Divider from '../../global/divider'
import FeaturedResource from '../../global/featuredResource'
import FlexibleHero from '../../global/flexibleHero'
import Hero from '../../global/hero'
import Icons from '../../global/icons'
import KeyStats from '../../global/keyStats'
import MainCTA from '../../global/mainCta'
import Marketo from '../../global/marketo'
import MediaText from '../../global/mediaText'
import ResourceRow from '../../global/resourceRow'
import SEOTags from '../../global/seoTags'
import Slider from '../../global/slider'
import SpeakerText from '../../global/speakerText'
import Tiles from '../../global/tiles'
import VideoSpotlight from '../../global/videoSpotlight'

// styles
import MarketoFormContainer from '../../global/marketo/style'

const EventsPage = ({
  path,
  pageContext,
  location,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  let dynamicPageContent, pageData: any

  let defaultLocale: string
  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }

  pageData = pageContext.eventsData

  if (pageData && pageData.flexSection) {
    const eventsPageContent = pageData.flexSection

    dynamicPageContent = eventsPageContent.map((module: any, i: Number) => {
      switch (module.__typename) {
        case ModuleNames.DIVIDER:
          return <Divider data={module} key={`page-component--${i}`}></Divider>

        case ModuleNames.MEDIA_ASSET:
          return (
            <MediaText
              data={module}
              mediaAsset={true}
              key={`page-component--${i}`}
              pageType="events"></MediaText>
          )

        case ModuleNames.CONTENT_ROWS:
          return <ContentRows data={module} key={`page-component--${i}`} />

        case ModuleNames.GLOBAL_KEY_STATS:
          return <KeyStats data={module} key={`page-component--${i}`}></KeyStats>

        case ModuleNames.VIDEO_SPOTLIGHT:
          return <VideoSpotlight data={module} key={`page-component--${i}`}></VideoSpotlight>

        case ModuleNames.GLOBAL_CONTENT_COLUMNS:
          return <ContentColumns data={module} key={`page-component--${i}`}></ContentColumns>

        case ModuleNames.GLOBAL_CONTENT_BLOCK:
          return <ContentBlock data={module} key={`page-component--${i}`}></ContentBlock>

        case ModuleNames.RESOURCE_ROW:
          return (
            <ResourceRow
              data={module}
              key={`page-component--${i}`}
              locale={defaultLocale}></ResourceRow>
          )

        case ModuleNames.ICONS:
          return <Icons data={module} key={`page-component--${i}`}></Icons>

        case ModuleNames.FEATURED_RESOURCE:
          return (
            <FeaturedResource
              data={module}
              pageLocale={defaultLocale}
              key={`page-component--${i}`}></FeaturedResource>
          )

        case ModuleNames.SLIDER:
          return <Slider data={module} key={`page-component--${i}`}></Slider>

        case ModuleNames.HERO:
          const pageTitle = pageData.name ? pageData.name : ''
          return (
            <Hero
              data={module}
              location={location}
              pageTitle={pageTitle}
              pageType="events"
              showBreadcrumbs={true}
              key={`page-component--${i}`}
              metaInformation={pageData.metaInformation}
              pageUseCase="Events"
              breadcrumb={pageData.breadcrumb}
              locale={defaultLocale}
            />
          )
        case ModuleNames.GLOBAL_MAIN_CTA:
          return <MainCTA data={module} key={`page-component--${i}`}></MainCTA>

        case ModuleNames.TILES:
          return <Tiles data={module} key={`page-component--${i}`}></Tiles>

        case ModuleNames.MARKETO_FORM:
          return (
            <MarketoFormContainer>
              <div>
                {module.title && <p className="FormTitle">{module.title}</p>}
                {module.subtitle && <p className="FormSubtitle">{module.subtitle}</p>}
                <Marketo formData={module.marketoForm} />
                {module.marketoFinePrint && (
                  <p className="FormFinePrint">{module.marketoFinePrint}</p>
                )}
              </div>
            </MarketoFormContainer>
          )

        case ModuleNames.SPEAKER_TEXT:
          return <SpeakerText data={module} key={`page-component--${i}`}></SpeakerText>

        case ModuleNames.FLEXIBLE_HERO:
          return (
            <FlexibleHero
              data={module}
              location={location}
              pageTitle={pageTitle}
              pageType="events"
              showBreadcrumbs={!!pageData.breadcrumb}
              key={`page-component--${i}`}
              pageUseCase="Events"
              breadcrumb={pageData.breadcrumb}
              locale={defaultLocale}
            />
          )
      }
    })
  }

  const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
  const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

  const pageInner = (
    <div data-cy={`EventsPage`}>
      <SEOTags
        seoMetadata={seoData}
        slug={defaultSlug}
        path={path}
        locale={defaultLocale}
        availableLocales={pageContext.availableLocales}
      />
      {dynamicPageContent}
    </div>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      customNav={pageData?.customHeaderNavigation}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      minimalHeader={pageData?.headerNavigationStyle !== 'Full'}
      minimalFooter={true}
      slug={defaultSlug}
      availableLocales={pageContext.availableLocales}>
      {pageInner}
    </Layout.Template>
  )
}

export default EventsPage
